export type ScannerMarketRow = {
  symbol: string;
  name?: string | null;
  sector?: string | null;
  close?: number | null;
  change?: number | null;
  change_pct?: number | null;
  volume?: number | null;
  date?: string | null;
  is_52_week_high?: boolean | null;
  is_52_week_low?: boolean | null;
  high_52?: number | null;
  low_52?: number | null;
};

export type CircuitRule = {
  type: "pct" | "abs";
  threshold: number;
};

export type CircuitStock = ScannerMarketRow & {
  previousClose: number | null;
  rule: CircuitRule | null;
};

export type MarketSnapshotStats = {
  fifty_two_week_high?: number | null;
  fifty_two_week_low?: number | null;
  lookback?: number | null;
};

export type MarketStats = {
  latestDate: string | null;
  stocksTraded: number;
  advances: number;
  declines: number;
  unchanged: number;
  upperCircuit: number;
  lowerCircuit: number;
  fiftyTwoWeekHigh: number;
  fiftyTwoWeekLow: number;
  upperCircuitStocks: CircuitStock[];
  lowerCircuitStocks: CircuitStock[];
  fiftyTwoWeekHighStocks: ScannerMarketRow[];
  fiftyTwoWeekLowStocks: ScannerMarketRow[];
};

const isNumber = (value: unknown): value is number => typeof value === "number" && !Number.isNaN(value);
const safeCount = (value: unknown): number => (typeof value === "number" && !Number.isNaN(value) ? value : 0);

const getCircuitRule = (previousClose: number): CircuitRule => {
  if (previousClose < 10) {
    return { type: "abs", threshold: 1 };
  }
  return { type: "pct", threshold: 10 };
};

export const computeMarketStats = (
  rows: ScannerMarketRow[],
  latestDate?: string | null,
  snapshotStats?: MarketSnapshotStats | null,
): MarketStats => {
  let advances = 0;
  let declines = 0;
  let unchanged = 0;
  const upperCircuitStocks: CircuitStock[] = [];
  const lowerCircuitStocks: CircuitStock[] = [];
  const fiftyTwoWeekHighStocks: ScannerMarketRow[] = [];
  const fiftyTwoWeekLowStocks: ScannerMarketRow[] = [];

  rows.forEach((row) => {
    const change = isNumber(row.change) ? row.change : null;
    const close = isNumber(row.close) ? row.close : null;
    const changePct = isNumber(row.change_pct) ? row.change_pct : null;
    const previousClose = close != null && change != null ? close - change : null;
    const computedChangePct =
      changePct != null
        ? changePct
        : previousClose != null && previousClose !== 0 && change != null
          ? (change / previousClose) * 100
          : null;

    if (computedChangePct != null) {
      if (computedChangePct > 0) advances += 1;
      else if (computedChangePct < 0) declines += 1;
      else unchanged += 1;
    } else if (change != null) {
      if (change > 0) advances += 1;
      else if (change < 0) declines += 1;
      else unchanged += 1;
    }

    if (change == null || close == null) return;
    if (!isNumber(previousClose) || previousClose <= 0) return;
    const rule = getCircuitRule(previousClose);

    const upperHit = rule.type === "abs"
      ? change >= rule.threshold
      : (computedChangePct != null && computedChangePct >= rule.threshold - 1e-6);
    const lowerHit = rule.type === "abs"
      ? change <= -rule.threshold
      : (computedChangePct != null && computedChangePct <= -rule.threshold + 1e-6);

    if (upperHit) {
      upperCircuitStocks.push({ ...row, change_pct: computedChangePct ?? row.change_pct ?? null, previousClose, rule });
    }
    if (lowerHit) {
      lowerCircuitStocks.push({ ...row, change_pct: computedChangePct ?? row.change_pct ?? null, previousClose, rule });
    }

    if (row.is_52_week_high === true) {
      fiftyTwoWeekHighStocks.push(row);
    }
    if (row.is_52_week_low === true) {
      fiftyTwoWeekLowStocks.push(row);
    }
  });

  const highCountFromSnapshot = safeCount(snapshotStats?.fifty_two_week_high);
  const lowCountFromSnapshot = safeCount(snapshotStats?.fifty_two_week_low);

  return {
    latestDate: latestDate || null,
    stocksTraded: rows.length,
    advances,
    declines,
    unchanged,
    upperCircuit: upperCircuitStocks.length,
    lowerCircuit: lowerCircuitStocks.length,
    fiftyTwoWeekHigh: highCountFromSnapshot || fiftyTwoWeekHighStocks.length,
    fiftyTwoWeekLow: lowCountFromSnapshot || fiftyTwoWeekLowStocks.length,
    upperCircuitStocks,
    lowerCircuitStocks,
    fiftyTwoWeekHighStocks,
    fiftyTwoWeekLowStocks,
  };
};
